import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable ,  merge, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { DateTableFilter } from './date-table-filter';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
/**
 * Abstract class for data sources
 * implement tipical operations: filtering, paging, sorting
 * implement CDK DataSource class
 */
export abstract class AbstractDataSource<T> implements DataSource<T> {
  public itemsSubject = new BehaviorSubject<T[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Returns data loading status on it change
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Data length from server
   */
  public length: number;
  /**
   * Data from server
   */
  public data: T[];

  public isLoading = false;

  /**
   * Can be of any type,
   * process in implementation of special data sorce for concret type
   */
  protected _filter = new DateTableFilter();
  /**
   * Get filter
   */
  get filter(): DateTableFilter {
    return this._filter;
  }
  /**
   * Set filter
   * reloading data from server after set
   */
  set filter(filter: DateTableFilter) {
    this._filter = { ...filter };
    this.loadData();
  }

  private _paginator: MatPaginator;
  /**
   * Get paginator
   */
  get paginator(): MatPaginator {
    return this._paginator;
  }
  /**
   * Set paginator
   * Applied after set
   */
  set paginator(paginator: MatPaginator) {
    this._paginator = paginator;
    this.tryMergePaginatorAndSort();
  }

  private _sort: MatSort;
  /**
   * Get matirial sort object
   */
  get sort(): MatSort {
    return this._sort;
  }
  /**
   * Set matirial sort object
   * Applied after set
   */
  set sort(sort: MatSort) {
    this._sort = sort;
    this.tryMergePaginatorAndSort();
  }

  constructor() { }

  /**
   * Connect data source
   *
   * @param collectionViewer data
   */
  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    return this.itemsSubject.asObservable();
  }

  /**
   * Disconnect data source
   *
   * @param collectionViewer data
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject.complete();
    this.loadingSubject.complete();
  }

  /**
   * Applying sorting and paging changes
   */
  tryMergePaginatorAndSort() {
    if (this._sort == null || this._paginator == null) {
      return;
    }
    this._sort.sortChange.subscribe(() => this._paginator.pageIndex = 0);
    merge(this._sort.sortChange, this._paginator.page)
      .pipe(
        tap(() => {
 this.loadData(); 
})
      ).subscribe();
  }
  /**
   * Load data from server with specific paging and sorting data
   */
  loadData() {
    const pageIndex = (this._paginator && this._paginator.pageIndex || 0) + 1;
    const pageSize = this._paginator && this._paginator.pageSize || 20;
    const sortField = this._sort && this._sort.active || 'id';
    const sortDirection = this._sort && this._sort.direction || 'asc';

    this.loadingSubject.next(true);

    this.isLoading = true;
    this.getData({ pageIndex, pageSize, sortField, sortDirection }).pipe(
      catchError(() => of([])),
      finalize(() => {
        this.loadingSubject.next(false);
        this.isLoading = false;
      })
    )
      .subscribe((items: any) => {
        this.length = items.length;
        this.data = items.data as T[];
        this.itemsSubject.next(this.data);
      });
  }
  /**
   * This method mast be ovverided in your data source,
   * here implements custom filter processing
   *
   * @param pagingAndSortingData paging and sorting data obtained by table
   */
  abstract getData(pagingAndSortingData: any): Observable<any>;


  decrementLength(length: number): void {
    this.length -= length;
  }

  incrementLength(length: number): void {
    this.length += length;
  }

}


export abstract class FlexibleAbstractDataSource<TList, TGet, TAdd, TUpdate> implements DataSource<TList> {
  public itemsSubject = new BehaviorSubject<TList[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Returns data loading status on it change
   */
  public loading$ = this.loadingSubject.asObservable();

  /**
   * Data length from server
   */
  public length: number;
  /**
   * Data from server
   */
  public data: TList[];

  public isLoading = false;

  /**
   * Can be of any type,
   * process in implementation of special data sorce for concret type
   */
  protected _filter = new DateTableFilter();
  /**
   * Get filter
   */
  get filter(): DateTableFilter {
    return this._filter;
  }
  /**
   * Set filter
   * reloading data from server after set
   */
  set filter(filter: DateTableFilter) {
    this._filter = { ...filter };
    this.loadData();
  }

  private _paginator: MatPaginator;
  /**
   * Get paginator
   */
  get paginator(): MatPaginator {
    return this._paginator;
  }
  /**
   * Set paginator
   * Applied after set
   */
  set paginator(paginator: MatPaginator) {
    this._paginator = paginator;
    this.tryMergePaginatorAndSort();
  }

  private _sort: MatSort;
  /**
   * Get matirial sort object
   */
  get sort(): MatSort {
    return this._sort;
  }
  /**
   * Set matirial sort object
   * Applied after set
   */
  set sort(sort: MatSort) {
    this._sort = sort;
    this.tryMergePaginatorAndSort();
  }

  constructor() { }

  /**
   * Connect data source
   *
   * @param collectionViewer data
   */
  connect(collectionViewer: CollectionViewer): Observable<TList[]> {
    return this.itemsSubject.asObservable();
  }

  /**
   * Disconnect data source
   *
   * @param collectionViewer data
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject.complete();
    this.loadingSubject.complete();
  }

  /**
   * Applying sorting and paging changes
   */
  tryMergePaginatorAndSort() {
    if (this._sort == null || this._paginator == null) {
      return;
    }
    this._sort.sortChange.subscribe(() => this._paginator.pageIndex = 0);
    merge(this._sort.sortChange, this._paginator.page)
      .pipe(
        tap(() => {
 this.loadData(); 
})
      ).subscribe();
  }
  /**
   * Load data from server with specific paging and sorting data
   */
  loadData() {
    const pageIndex = (this._paginator && this._paginator.pageIndex || 0) + 1;
    const pageSize = this._paginator && this._paginator.pageSize || 20;
    const sortField = this._sort && this._sort.active || 'id';
    const sortDirection = this._sort && this._sort.direction || 'asc';

    this.loadingSubject.next(true);

    this.isLoading = true;
    this.getData({ pageIndex, pageSize, sortField, sortDirection }).pipe(
      catchError(() => of([])),
      finalize(() => {
        this.loadingSubject.next(false);
        this.isLoading = false;
      })
    )
      .subscribe((items: any) => {
        this.length = items.length;
        this.data = items.data as TList[];
        this.itemsSubject.next(this.data);
      });
  }
  /**
   * This method mast be ovverided in your data source,
   * here implements custom filter processing
   *
   * @param pagingAndSortingData paging and sorting data obtained by table
   */
  abstract getData(pagingAndSortingData: any): Observable<any>;


  decrementLength(length: number): void {
    this.length -= length;
  }

  incrementLength(length: number): void {
    this.length += length;
  }

}
